import { useState, useMemo } from "react";

/**
 * Custom hook for searching data with specific keys.
 * @param {Array} initialData - The initial data to be searched.
 * @param {Array} searchKeys - An array of keys to search within the data.
 * @param {Object} searchValueFns - Optional map of key → (value, row) => string for columns whose
 *   display value differs from the raw data property (e.g. supplierId → supplierName).
 * @returns {Array} - A tuple containing the filtered data and a function to update the search query.
 */
export function useSearchData(initialData, searchKeys, searchValueFns = {}) {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredData = useMemo(() => {
        if (!searchQuery || searchKeys.length === 0) {
            return initialData;  // Return all data if query is empty or no keys specified
        }

        const itemContains = (item) => {
            return searchKeys.some(key => {
                const hasFn = typeof searchValueFns[key] === 'function';
                const value = item[key];
                // For virtual columns (no matching property on the row), only proceed if there's a
                // searchValue fn to extract the text — it will receive undefined and handle it itself.
                if (!hasFn && (value === null || value === undefined)) return false;
                const searchText = hasFn
                    ? String(searchValueFns[key](value, item) ?? '')
                    : value.toString();
                return searchText.toLowerCase().includes(searchQuery.toLowerCase());
            });
        };

        const filterItems = (items) => {
            return items.filter(item => {
                if (itemContains(item)) return true;
                if (item.items && Array.isArray(item.items)) {
                    // If we have child items, and we haven't found a match on the parent, check the children

                    return item.items.some(childItem => itemContains(childItem));
                }
                return false;
            });
        };

        const filteredTopLevelItems = filterItems(initialData);

        return filteredTopLevelItems.map(item => {
            if (itemContains(item)) return item; // The search matches the parent item; include all children
            if (item.items && Array.isArray(item.items)) {
                // Search term did not match parent, but it must have matched some children; filter children accordingly
                return { ...item, items: filterItems(item.items) };
            }
            return item;
        });
    }, [initialData, searchQuery, searchKeys, searchValueFns]);

    // Function to update the search query
    const setSearch = query => {
        setSearchQuery(query);
    };

    return [filteredData, setSearch];
}